import React from "react";
import { DataCyStrings } from "../../types/DataCyStrings";
import getTranslator from "../../utility/getTranslator";
import { getReadableTimestamp } from "../../utility";
import type { PreorderInfo } from "../../utility/getPreorderInfo";
import { SVG } from "../svg";
import { $PreorderCallout, $PreorderBoxedCallout, $PreorderInline } from "./styles";

const useTranslator = getTranslator("PreorderLabel");

export type PreorderLabelVariant = "callout" | "boxedCallout" | "inline";

interface PreorderLabelProps {
  preorderInfo: PreorderInfo | null | undefined;
  variant: PreorderLabelVariant;
  // When true (inline variant only), always show the compact "(Preorder)"
  // label even if a shipping date is available. Used on Return Preview,
  // where the design requires only the tag next to the price.
  compact?: boolean;
}

/**
 * Renders a preorder indicator next to (or below) an exchange item. When no
 * preorder info is present, renders nothing. Degrades gracefully when the
 * shipping date is absent or malformed — the status-only label is still shown.
 */
const PreorderLabel = ({ preorderInfo, variant, compact }: PreorderLabelProps) => {
  const { t, i18n } = useTranslator();
  if (!preorderInfo || !preorderInfo.preorderStatus) return null;

  // Parse as local-midnight (no trailing Z). The backend sends a calendar
  // date (YYYY-MM-DD); appending "Z" makes it UTC midnight, which renders as
  // the previous day for any user west of UTC once Intl.DateTimeFormat
  // converts to local time.
  const formattedDate = preorderInfo.preorderShippingDate
    ? getReadableTimestamp(`${preorderInfo.preorderShippingDate}T00:00:00`, i18n.language)
    : "";

  if (variant === "boxedCallout") {
    return (
      <$PreorderBoxedCallout data-cy={DataCyStrings.preorderLabel} role="status">
        <span className="preorder-icon" aria-hidden="true">
          <SVG name="info" />
        </span>
        <span className="preorder-text">
          <strong>{formattedDate ? `${t("label")}:` : t("label")}</strong>
          {formattedDate ? <>{" "}{t("estimatedRestock", { date: formattedDate })}</> : null}
        </span>
      </$PreorderBoxedCallout>
    );
  }

  if (variant === "callout") {
    return (
      <$PreorderCallout data-cy={DataCyStrings.preorderLabel} role="status">
        <span className="preorder-text">
          <strong>{formattedDate ? `${t("label")}:` : t("label")}</strong>
          {formattedDate ? <>{" "}{t("estimatedRestock", { date: formattedDate })}</> : null}
        </span>
      </$PreorderCallout>
    );
  }

  const inlineText = formattedDate && !compact
    ? t("fullLabel", { date: formattedDate })
    : t("compactLabel");

  return (
    <$PreorderInline data-cy={DataCyStrings.preorderLabelInline}>
      {inlineText}
    </$PreorderInline>
  );
};

export default PreorderLabel;
